import {
  HomeOutlined,
  RiseOutlined,
  TeamOutlined,
  WarningOutlined
} from "@ant-design/icons"
import { Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { AnalyticsInsights } from "../redux/global-map.types"
import { REGION_LABELS } from "./analytics-constants"

interface KeyInsightsRowProps {
  insights: AnalyticsInsights
}

interface InsightCardSpec {
  key: string
  icon: React.ReactNode
  color: string
  bg: string
  title: string
  value: React.ReactNode
  supporting: React.ReactNode
  available: boolean
}

/**
 * Spec §10: deterministic, calculated-only text — no LLM, no narrative
 * claims beyond the numbers. Workforce Utilization/SLA At Risk render the
 * spec's own "Data unavailable" state (§14) rather than an estimate, per
 * `insights.workforceUtilization.available`/`slaAtRisk.available` — both
 * `false` today (architecture doc §4: no backing data anywhere).
 */
export function KeyInsightsRow({ insights }: KeyInsightsRowProps) {
  const { t } = useTranslation()

  const leadGrowthSupport =
    insights.leadGrowth.supportingRegions.length > 0
      ? t("globalMap.analytics.insights.leadGrowthSupport", {
          regions: insights.leadGrowth.supportingRegions
            .map((region) => REGION_LABELS[region] ?? region)
            .join(", ")
        })
      : t("globalMap.analytics.insights.noRegionData")

  const cards: InsightCardSpec[] = [
    {
      key: "leadGrowth",
      icon: <RiseOutlined />,
      color: "#16a34a",
      bg: "#f0fdf4",
      title: t("globalMap.analytics.insights.leadGrowthTitle"),
      value:
        insights.leadGrowth.changePercent === null
          ? "—"
          : `${insights.leadGrowth.changePercent > 0 ? "+" : ""}${insights.leadGrowth.changePercent}%`,
      supporting: leadGrowthSupport,
      available: insights.leadGrowth.available
    },
    {
      key: "activeProjects",
      icon: <HomeOutlined />,
      color: "#1677ff",
      bg: "#eff6ff",
      title: t("globalMap.analytics.insights.activeProjectsTitle"),
      value: insights.activeProjects.count,
      supporting: insights.activeProjects.topRegion
        ? t("globalMap.analytics.insights.activeProjectsSupport", {
            region:
              REGION_LABELS[insights.activeProjects.topRegion] ??
              insights.activeProjects.topRegion
          })
        : t("globalMap.analytics.insights.noRegionData"),
      available: insights.activeProjects.available
    },
    {
      key: "workforceUtilization",
      icon: <TeamOutlined />,
      color: "#f97316",
      bg: "#fff7ed",
      title: t("globalMap.analytics.insights.workforceUtilizationTitle"),
      value: "—",
      supporting: t("globalMap.tabs.comingSoon"),
      available: false
    },
    {
      key: "slaAtRisk",
      icon: <WarningOutlined />,
      color: "#dc2626",
      bg: "#fef2f2",
      title: t("globalMap.analytics.insights.slaAtRiskTitle"),
      value: "—",
      supporting: t("globalMap.tabs.comingSoon"),
      available: false
    }
  ]

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 min-[1024px]:grid-cols-4">
      {cards.map((card) => {
        const body = (
          <div
            key={card.key}
            className="flex flex-col gap-2 rounded-lg border border-[#E3E8EF] bg-white p-3"
          >
            <span
              aria-hidden
              className="flex size-8 items-center justify-center rounded-md"
              style={{ color: card.color, backgroundColor: card.bg }}
            >
              {card.icon}
            </span>
            <div className="text-xs font-medium text-slate-600">
              {card.title}
            </div>
            <div className="text-lg font-bold text-gray-900">{card.value}</div>
            <div
              className="rounded px-2 py-1.5 text-[11px] leading-snug"
              style={{
                backgroundColor: card.available ? card.bg : "#f8fafc",
                color: "#475569"
              }}
            >
              {card.supporting}
            </div>
          </div>
        )
        return card.available ? (
          body
        ) : (
          <Tooltip key={card.key} title={t("globalMap.tabs.comingSoon")}>
            {body}
          </Tooltip>
        )
      })}
    </div>
  )
}
